import { TrackPick } from "../TrackPick";
import { ForwardCupModeSet } from "./CupMode/ForwardCupModeSet";
import { ReverseCupModeSet } from "./CupMode/ReverseCupModeSet";
import { MatchmakingSet } from "./Matchmaking/MatchmakingSet";
import { TimeAttackSet } from "./TimeAttack/TimeAttackSet";
import { WorldTourSet } from "./WorldTour/WorldTourSet";
import {
  MatchTrack,
  matchTrackHasData,
  matchTrackHasDriverData,
} from "../Track/MatchTrack";
import { checkTypeExhausted, nonUndefined } from "Utility";
import { RoundsSet } from "./Rounds/RoundsSet";
import { DriverTime } from "../DriverTime";
import { getTimeAttackRoundDriverTimes } from "./TimeAttack/Track/Round/TimeAttackRound";
import {
  getForwardCupModeTrackRosterDisplayScore,
  getReverseCupModeTrackRosterDisplayScore,
} from "./CupMode/Track/CupModeTrack";
import { getRoundsTrackRosterDisplayScore } from "./Rounds/Track/RoundsTrack";
import { TrackRound } from "../Track/TrackRound";
import { Scores } from "../../Scores";
import { Positions } from "../../Positions";
import { ForwardCupModeLongSet } from "./CupMode/ForwardCupModeLongSet";
import { getForwardCupModeLongTrackRosterDisplayScore } from "./CupMode/Track/CupModeLongTrack";
import { SetPointRules } from "model/Match/Set/SetPointRules";
import { KnockoutSet } from "./Knockout/KnockoutSet";
import { getKnockoutTrackRosterDisplayScore } from "./Knockout/Track/KnockoutTrack";
import { getResults } from "model/Results";
import { RoundsKnockoutSet } from "./RoundsKnockout/RoundsKnockoutSet";

export const SET_TYPE_ABBREVIATIONS: { [key in MatchSetType]: string } = {
  ForwardCupMode: "CUP",
  ForwardCupModeLong: "CUPL",
  Knockout: "KO",
  Matchmaking: "MM",
  ReverseCupMode: "RCUP",
  Rounds: "RND",
  RoundsKnockout: "RNDKO",
  TimeAttack: "TA",
  WorldTour: "WT",
};

export type MatchSetBase = {
  finalPositions: Positions | undefined;
  isComplete: boolean;
  isTiebreaker: boolean;
  picks: TrackPick[];
  points: SetPointRules | undefined;
  scores: Scores | undefined;
};

export type MatchSet =
  | ForwardCupModeSet
  | ForwardCupModeLongSet
  | KnockoutSet
  | MatchmakingSet
  | ReverseCupModeSet
  | RoundsSet
  | RoundsKnockoutSet
  | TimeAttackSet
  | WorldTourSet;

export type NonTAMatchSet =
  | ForwardCupModeSet
  | ForwardCupModeLongSet
  | KnockoutSet
  | MatchmakingSet
  | ReverseCupModeSet
  | RoundsSet
  | RoundsKnockoutSet
  | WorldTourSet;

// todo - get from sets
export type MatchSetType =
  | "ForwardCupMode"
  | "ForwardCupModeLong"
  | "Knockout"
  | "Matchmaking"
  | "ReverseCupMode"
  | "Rounds"
  | "RoundsKnockout"
  | "TimeAttack"
  | "WorldTour";

export type TrackBasedMatchSet = MatchmakingSet | WorldTourSet;

export const matchSetHasData = (set: MatchSet) => {
  if (!!set.scores && Object.values(set.scores).some((s) => !!s)) {
    return true;
  }

  return set.tracks.some(matchTrackHasData);
};

export const matchSetHasDriverData = (set: MatchSet) => {
  return set.tracks.some(matchTrackHasDriverData);
};

export const getMatchSetWinnerIds = (set: MatchSet) => {
  if (!set.isComplete) {
    return undefined;
  }

  const results = getResults(set.finalPositions, set.format.numWinners);
  if (!results) {
    return undefined;
  }

  return Object.keys(results).filter((id) => results[id] === "W");
};

export const getMatchSetDisplayScores = (set: MatchSet) => {
  switch (set.type) {
    case "ForwardCupMode":
    case "Knockout":
    case "ReverseCupMode":
    case "Rounds":
    case "RoundsKnockout":
      return set.scores ?? {};
    case "ForwardCupModeLong":
    case "Matchmaking":
    case "WorldTour":
      if (set.format.numTracks === 1) {
        const track = set.tracks[0];
        if (!track) {
          return set.scores;
        }

        return track.scores;
      }

      return set.scores;
    case "TimeAttack":
      return set.scores;
    default:
      return checkTypeExhausted(set);
  }
};

export const getMatchSetRosterDisplayScore = (
  set: MatchSet,
  rosterId: string
) => {
  const scores = getMatchSetDisplayScores(set);
  if (!scores) {
    return "-";
  }

  return scores[rosterId]?.toString() ?? "-";
};

export const getMatchSetTracksRosterDisplayScores = (
  set: MatchSet,
  rosterId: string
) => {
  switch (set.type) {
    case "ForwardCupMode":
      return nonUndefined(
        set.tracks.map((t) =>
          getForwardCupModeTrackRosterDisplayScore(
            t,
            rosterId,
            set.format.finalist,
            set.format.pointsLimit
          )
        )
      );
    case "ForwardCupModeLong":
      return nonUndefined(
        set.tracks.map((t) =>
          getForwardCupModeLongTrackRosterDisplayScore(
            t,
            rosterId,
            set.format.finalist,
            set.format.pointsLimit
          )
        )
      );
    case "Knockout":
      return nonUndefined(
        set.tracks.map((t) => getKnockoutTrackRosterDisplayScore(t, rosterId))
      );
    case "ReverseCupMode":
      return nonUndefined(
        set.tracks.map((t) =>
          getReverseCupModeTrackRosterDisplayScore(
            t,
            rosterId,
            set.format.lastChance
          )
        )
      );
    case "Rounds":
    case "RoundsKnockout":
      return nonUndefined(
        set.tracks.map((t) => getRoundsTrackRosterDisplayScore(t, rosterId))
      );
    case "Matchmaking":
    case "WorldTour":
      return set.tracks.map((t) => t.scores[rosterId].toString());
    case "TimeAttack":
      return undefined;
    default:
      return checkTypeExhausted(set);
  }
};

export const getMatchSetTrackHeaders = (set: MatchSet) => {
  switch (set.type) {
    case "ForwardCupMode":
    case "ForwardCupModeLong":
    case "Knockout":
    case "Matchmaking":
    case "ReverseCupMode":
    case "Rounds":
    case "RoundsKnockout":
    case "WorldTour":
      return [...set.tracks.map((_, i) => `${i + 1}`)];
    case "TimeAttack":
      return undefined;
    default:
      return checkTypeExhausted(set);
  }
};

export const getMatchSetDriverTimes = (
  set: MatchSet,
  trackId?: string,
  driverIds?: string[]
): DriverTime[] => {
  switch (set.type) {
    case "ForwardCupMode":
    case "ForwardCupModeLong":
    case "Knockout":
    case "Matchmaking":
    case "ReverseCupMode":
    case "Rounds":
    case "RoundsKnockout":
    case "WorldTour":
      return (set.tracks as MatchTrack[])
        .filter((t) => !trackId || t.trackId === trackId)
        .flatMap((t) => t.rounds as TrackRound[])
        .flatMap((r) => r.times)
        .filter((t) => !driverIds || driverIds.includes(t.driverId));
    case "TimeAttack":
      return set.tracks
        .filter((t) => t.trackId === trackId)
        .flatMap((t) => t.rounds)
        .flatMap(getTimeAttackRoundDriverTimes)
        .filter((t) => !driverIds || driverIds.includes(t.driverId));
    default:
      checkTypeExhausted(set);
      return [];
  }
};
